import type { SkillPackageLimits } from '../../generated/directorSkillTypes'

export type SkillReferenceInputErrorCode = 'invalid-path' | 'reserved-path' | 'not-markdown' | 'not-utf8' | 'too-large' | 'collision'

export class SkillReferenceInputError extends Error {
  constructor(
    public readonly code: SkillReferenceInputErrorCode,
    public readonly path?: string,
    public readonly limit?: number,
    public readonly actual?: number,
  ) {
    super(code)
    this.name = 'SkillReferenceInputError'
  }
}

export interface PreparedSkillReference {
  file: File
  path: string
  text: string
}

export interface SkillPackageUsage {
  documentBytes: number
  uploadFileCount: number
  userFileCount: number
  userPayloadBytes: number
  files: ReadonlyArray<{ path: string; sizeBytes: number; role: 'reference' | 'cover' }>
}

export type SkillPackageLimitViolation =
  | { kind: 'document'; limit: number; actual: number }
  | { kind: 'reference'; path: string; limit: number; actual: number }
  | { kind: 'file-count'; limit: number; actual: number }
  | { kind: 'payload'; limit: number; actual: number }

function pathSegments(path: string): string[] {
  return path.split('/')
}

export function canonicalizeSkillReferencePath(rawPath: string, limits: SkillPackageLimits): string {
  const normalized = rawPath
  if (
    !normalized ||
    normalized !== normalized.normalize('NFC') ||
    normalized.startsWith('/') ||
    normalized.includes('\\') ||
    normalized.includes('//') ||
    [...normalized].some((character) => character.charCodeAt(0) <= 0x1f)
  ) {
    throw new SkillReferenceInputError('invalid-path', rawPath)
  }
  const parts = pathSegments(normalized).map((part) => part.trim())
  if (parts.some((part) => !part || part === '.' || part === '..')) {
    throw new SkillReferenceInputError('invalid-path', rawPath)
  }
  if (parts.some((part) => part.toLowerCase() === '__dreamwright__')) {
    throw new SkillReferenceInputError('reserved-path', rawPath)
  }

  const suffix = limits.reference_suffix
  let leaf = parts[parts.length - 1]
  while (leaf.toLowerCase().endsWith(suffix)) {
    leaf = leaf.slice(0, -suffix.length)
  }
  if (!leaf) throw new SkillReferenceInputError('invalid-path', rawPath)
  parts[parts.length - 1] = `${leaf}${suffix}`
  const canonical = parts.join('/')
  if (new TextEncoder().encode(canonical).length > 240) {
    throw new SkillReferenceInputError('invalid-path', rawPath)
  }
  if (canonical.toLowerCase() === limits.document_filename.toLowerCase()) {
    throw new SkillReferenceInputError('reserved-path', canonical)
  }
  return canonical
}

export function hasSkillPathCollision(paths: Iterable<string>, candidate: string): boolean {
  const folded = candidate.toLowerCase()
  for (const path of paths) {
    if (path.toLowerCase() === folded) return true
  }
  return false
}

export async function readStrictUtf8(file: File): Promise<string> {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(await file.arrayBuffer())
  } catch {
    throw new SkillReferenceInputError('not-utf8', file.name)
  }
}

export async function prepareSkillReference(file: File, rawPath: string, limits: SkillPackageLimits): Promise<PreparedSkillReference> {
  if (!file.name.toLowerCase().endsWith(limits.reference_suffix)) {
    throw new SkillReferenceInputError('not-markdown', rawPath)
  }
  if (file.size > limits.max_reference_file_bytes) {
    throw new SkillReferenceInputError('too-large', rawPath, limits.max_reference_file_bytes, file.size)
  }
  const path = canonicalizeSkillReferencePath(rawPath, limits)
  const text = await readStrictUtf8(file)
  return {
    file: new File([file], pathSegments(path).pop() || path, {
      type: limits.reference_media_type,
    }),
    path,
    text,
  }
}

export function calculateSkillPackageUsage(
  contentMd: string,
  files: ReadonlyArray<{ sizeBytes: number; path?: string; role?: 'reference' | 'cover' }>,
): SkillPackageUsage {
  const documentBytes = new TextEncoder().encode(contentMd).length
  const uploadFileCount = files.length
  return {
    documentBytes,
    uploadFileCount,
    userFileCount: uploadFileCount + 1,
    userPayloadBytes: documentBytes + files.reduce((total, file) => total + file.sizeBytes, 0),
    files: files.map((file, index) => ({
      path: file.path ?? `reference-${index + 1}.md`,
      sizeBytes: file.sizeBytes,
      role: file.role ?? 'reference',
    })),
  }
}

export function findSkillPackageLimitViolation(usage: SkillPackageUsage, limits: SkillPackageLimits): SkillPackageLimitViolation | null {
  if (usage.documentBytes > limits.max_document_bytes) {
    return {
      kind: 'document',
      limit: limits.max_document_bytes,
      actual: usage.documentBytes,
    }
  }
  const oversizedReference = usage.files.find((file) => file.role === 'reference' && file.sizeBytes > limits.max_reference_file_bytes)
  if (oversizedReference) {
    return {
      kind: 'reference',
      path: oversizedReference.path,
      limit: limits.max_reference_file_bytes,
      actual: oversizedReference.sizeBytes,
    }
  }
  if (usage.uploadFileCount > limits.max_upload_file_count) {
    return {
      kind: 'file-count',
      limit: limits.max_upload_file_count,
      actual: usage.uploadFileCount,
    }
  }
  if (usage.userFileCount > limits.max_user_file_count) {
    return {
      kind: 'file-count',
      limit: limits.max_user_file_count,
      actual: usage.userFileCount,
    }
  }
  if (usage.userPayloadBytes > limits.max_user_payload_bytes) {
    return {
      kind: 'payload',
      limit: limits.max_user_payload_bytes,
      actual: usage.userPayloadBytes,
    }
  }
  return null
}
